import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Descriptions, InputNumber, Select, Spin, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import { errorText, openBlob } from "../api";
import { formatPeriod } from "../shared/money";
import Amount from "../shared/Amount";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import PageToolbar from "../../../../design-system/PageToolbar";
import { employeesChecklist, lohnkonto, lohnkontoPdfBlob } from "./api";

/** Employee picker shared by the Lohnkonto and the certificates tab. */
export const useEmployees = () => {
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    employeesChecklist()
      .then(res => setEmployees((res.data.rows || []).map(r => r.employee)))
      .catch(() => setEmployees([]));
  }, []);
  return employees;
};

const Lohnkonto = () => {
  const { t } = useTranslation("accounting");
  const employees = useEmployees();
  const [employeeId, setEmployeeId] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await lohnkonto(employeeId, year);
      setData(res.data);
    } catch (err) {
      message.error(errorText(err));
    }
    setLoading(false);
  }, [employeeId, year]);

  useEffect(() => {
    load();
  }, [load]);

  const money = (key, pick) => ({
    dataIndex: key,
    align: "end",
    render: (_, r) => (r.present ? <Amount cents={pick ? pick(r) : r[key]} /> : "")
  });
  const columns = [
    {
      title: t("Month"),
      dataIndex: "period",
      render: (v, r) => (
        <span>
          {formatPeriod(v)}{" "}
          {r.present && !r.locked ? <Tag color="orange">{t("preliminary")}</Tag> : null}
          {r.minijob ? <Tag color="purple">{t("Minijob")}</Tag> : null}
        </span>
      )
    },
    { title: t("StKl"), dataIndex: "steuerklasse" },
    { title: t("Bruttoarbeitslohn"), ...money("bruttoarbeitslohn") },
    { title: t("Tax-free"), ...money("steuerfreieBezuege") },
    { title: t("Sachbezug"), ...money("sachbezuege") },
    { title: t("VWL"), ...money("vwl") },
    { title: t("Gesamtbrutto"), ...money("gesamtbrutto") },
    { title: t("Steuerbrutto"), ...money("steuerbrutto") },
    { title: t("Lohnsteuer"), ...money("lohnsteuer") },
    { title: t("Soli"), ...money("solidaritaetszuschlag") },
    { title: t("KiSt"), ...money("kirchensteuer") },
    { title: t("SV-Brutto RV"), ...money("svBruttoRv", r => r.svBrutto.rv) },
    { title: t("SV employee"), ...money("eeTotal", r => r.employee.total) },
    { title: t("Net"), ...money("netto") },
    { title: t("Payout"), ...money("auszahlung") },
    { title: t("SV employer"), ...money("erTotal", r => r.employer.total) }
  ];
  const sumRow = data
    ? { ...data.sums, period: t("Year"), present: true, locked: true, steuerklasse: "" }
    : null;

  return (
    <Spin spinning={loading}>
      <PageToolbar
        filters={
          <>
            <Select
              showSearch
              placeholder={t("Employee")}
              optionFilterProp="label"
              value={employeeId}
              onChange={setEmployeeId}
              options={employees.map(e => ({
                value: e.id,
                label: `${e.name}${e.employeeId ? ` (${e.employeeId})` : ""}`
              }))}
            />
            <InputNumber
              value={year}
              onChange={v => v && setYear(v)}
              min={2020}
              max={2100}
              aria-label={t("Year")}
            />
          </>
        }
        actions={
          <Button
            disabled={!data}
            onClick={() =>
              openBlob(lohnkontoPdfBlob(employeeId, year), `Lohnkonto_${year}.pdf`).catch(err =>
                message.error(errorText(err))
              )
            }
          >
            {t("PDF")}
          </Button>
        }
      />
      <Card size="small" title={t("Lohnkonto (§ 4 LStDV)")}>
        {data ? (
          <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }} style={{ marginBottom: 12 }}>
            <Descriptions.Item label={t("Employee")}>
              {data.employee.name} {data.employee.employeeId ? `(${data.employee.employeeId})` : ""}
            </Descriptions.Item>
            <Descriptions.Item label={t("Steuer-ID")}>
              {data.employee.steuerId || "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("SV number")}>
              {data.employee.sozialversicherungsnummer || "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Krankenkasse")}>
              {data.employee.krankenkasse || "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Entry / exit")}>
              {data.employee.startDate || "—"} / {data.employee.endDate || "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Betriebsnummer")}>
              {data.employer.betriebsnummer || "—"}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
        <ResponsiveTable
          mobileLayout="table"
          className="acct-grid"
          rowKey="period"
          size="small"
          pagination={false}
          dataSource={data ? [...data.months, sumRow] : []}
          columns={columns}
          rowClassName={r => (r.period === t("Year") ? "ant-table-row-selected" : "")}
        />
      </Card>
    </Spin>
  );
};

export default Lohnkonto;
